import { createConnectorConfig } from '@command-center/connector-sdk';
import { createPortalApp, type PortalAppConfig } from '@command-center/core-platform';
import { createTableDefinition } from '@command-center/data-sdk';
import { createPortalModule } from '@command-center/module-sdk';

const tasksModule = createPortalModule({
  id: 'oneup.tasks',
  label: 'Tasks',
  route: '/tasks',
  icon: 'CheckSquare',
  navGroup: 'Work',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const leadsModule = createPortalModule({
  id: 'oneup.leads',
  label: 'Leads',
  route: '/pipeline/leads',
  icon: 'UserPlus',
  navGroup: 'Pipeline',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const clientsModule = createPortalModule({
  id: 'oneup.clients',
  label: 'Organizations',
  route: '/clients',
  icon: 'Building2',
  navGroup: 'Clients',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const copilotModule = createPortalModule({
  id: 'oneup.copilot',
  label: 'Today',
  route: '/command-center',
  icon: 'LayoutDashboard',
  navGroup: 'Command Center',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable', 'oneup-openai-logs'],
});

const whiteboardModule = createPortalModule({
  id: 'oneup.whiteboard',
  label: 'Whiteboard',
  route: '/whiteboard',
  icon: 'LayoutDashboard',
  navGroup: 'Knowledge',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable', 'oneup-openai-logs'],
});

const websitesModule = createPortalModule({
  id: 'oneup.websites',
  label: 'Websites',
  route: '/websites',
  icon: 'Globe',
  navGroup: 'Clients',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const proposalsModule = createPortalModule({
  id: 'oneup.proposals',
  label: 'Proposals',
  route: '/proposals',
  icon: 'FileText',
  navGroup: 'Pipeline',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const usersModule = createPortalModule({
  id: 'oneup.users',
  label: 'Contacts',
  route: '/users',
  icon: 'Users',
  navGroup: 'Clients',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const formsModule = createPortalModule({
  id: 'oneup.forms',
  label: 'Forms',
  route: '/clients/forms',
  icon: 'ClipboardList',
  navGroup: 'Clients',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const activityModule = createPortalModule({
  id: 'oneup.activity',
  label: 'Activity',
  route: '/activity',
  icon: 'Activity',
  navGroup: 'Command Center',
  appTypes: ['admin'],
});

const settingsModule = createPortalModule({
  id: 'oneup.settings',
  label: 'Settings',
  route: '/settings',
  icon: 'Settings',
  navGroup: 'Settings',
  appTypes: ['admin'],
});

const invoicesModule = createPortalModule({
  id: 'oneup.invoices',
  label: 'Invoices',
  route: '/billing/invoices',
  icon: 'Receipt',
  navGroup: 'Billing',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const subscriptionsModule = createPortalModule({
  id: 'oneup.subscriptions',
  label: 'Subscriptions',
  route: '/billing/subscriptions',
  icon: 'Repeat',
  navGroup: 'Billing',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const paymentsModule = createPortalModule({
  id: 'oneup.payments',
  label: 'Payments',
  route: '/billing/payments',
  icon: 'CreditCard',
  navGroup: 'Billing',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const agentsModule = createPortalModule({
  id: 'oneup.agents',
  label: 'Agents',
  route: '/ai-systems/agents',
  icon: 'Bot',
  navGroup: 'AI Systems',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const callsModule = createPortalModule({
  id: 'oneup.calls',
  label: 'Calls',
  route: '/ai-systems/calls',
  icon: 'PhoneCall',
  navGroup: 'AI Systems',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

const promptsModule = createPortalModule({
  id: 'oneup.prompts',
  label: 'Prompts',
  route: '/ai-systems/prompts',
  icon: 'MessageSquareText',
  navGroup: 'AI Systems',
  appTypes: ['admin'],
  requiredConnectors: ['oneup-airtable'],
});

export const config: PortalAppConfig = {
  appId: 'admin-oneup',
  appType: 'admin',
  clientId: 'oneup',
  clientName: 'One Up',
  branding: {
    name: 'One Up Command Center',
    icon: '1U',
    colors: {
      primary: '#79141a',
    },
  },
  navigationVariant: 'workflow-groups',
  collapsibleNavigationGroups: true,
  alice: {
    enabled: true,
    defaultState: 'open',
    assistantName: 'Alice',
    assistantSubtitle: 'Context-aware help for this workspace.',
    avatarPath: '/MyNameIsNotAliceAvatar.png',
    defaultModelLabel: 'Default',
    portalScope: 'internal',
    allowedContextSources: [
      'page',
      'form',
      'form_entry',
      'task',
      'organization',
      'contact',
      'communication',
      'invoice',
      'subscription',
      'payment',
      'agent',
      'call',
      'prompt',
      'proposal',
      'project',
      'website',
      'vault_document',
      'user',
      'manual',
    ],
    allowedActions: [
      'read_context',
      'draft_text',
      'suggest_field_values',
      'update_visible_draft_field',
      'open_center_workspace',
      'open_quick_action_drawer',
    ],
    blockedActions: [
      'send_email_without_approval',
      'delete_records',
      'publish_changes',
      'charge_client',
      'change_permissions',
      'access_unscoped_client_data',
    ],
    enabledModes: ['global', 'context', 'composer'],
    quickActionsEnabled: true,
    contextBarEnabled: true,
    suggestedActionsEnabled: true,
    voiceEnabledPlaceholder: true,
    fileUploadEnabledPlaceholder: true,
    cameraEnabledPlaceholder: true,
  },
  navigation: [
    {
      id: 'command-center-today',
      label: 'Today',
      route: '/command-center',
      icon: 'LayoutDashboard',
      group: 'Command Center',
      moduleId: 'oneup.copilot',
      description: 'Morning open and end-of-day close',
    },
    {
      id: 'switchboard',
      label: 'Switchboard',
      route: '/command-center/switchboard',
      icon: 'Rocket',
      group: 'Command Center',
      moduleId: 'oneup.copilot',
      description: 'Launch common Alice workflows',
    },
    {
      id: 'activity',
      label: 'Activity',
      route: '/activity',
      icon: 'Activity',
      group: 'Command Center',
      moduleId: 'oneup.activity',
      description: 'Recent log entries',
    },
    {
      id: 'leads',
      label: 'Leads',
      route: '/pipeline/leads',
      icon: 'UserPlus',
      group: 'Pipeline',
      moduleId: 'oneup.leads',
      sourceName: 'Leads',
      description: 'Prospect intake and opportunity review',
    },
    {
      id: 'proposals',
      label: 'Proposals',
      route: '/proposals',
      icon: 'FileText',
      group: 'Pipeline',
      moduleId: 'oneup.proposals',
      description: 'Proposal workflow',
    },
    {
      id: 'organizations',
      label: 'Organizations',
      route: '/clients',
      icon: 'Building2',
      group: 'Clients',
      moduleId: 'oneup.clients',
      sourceName: 'ENT_Organizations',
      description: 'Manage organization records and status',
    },
    {
      id: 'contacts',
      label: 'Contacts',
      route: '/users',
      icon: 'Users',
      group: 'Clients',
      moduleId: 'oneup.users',
      sourceName: 'ENT_Users',
      description: 'Manage client contacts and access roles',
    },
    {
      id: 'websites',
      label: 'Websites',
      route: '/websites',
      icon: 'Globe',
      group: 'Clients',
      moduleId: 'oneup.websites',
      sourceName: 'ENT_Websites',
      description: 'Manage website inventory and health',
    },
    {
      id: 'forms',
      label: 'Forms',
      route: '/clients/forms',
      icon: 'ClipboardList',
      group: 'Clients',
      moduleId: 'oneup.forms',
      sourceName: 'Forms / Submissions',
      description: 'Client and prospect form submissions',
    },
    {
      id: 'tasks',
      label: 'Tasks',
      route: '/tasks',
      icon: 'CheckSquare',
      group: 'Work',
      moduleId: 'oneup.tasks',
      sourceName: 'ENT_Projects',
      description: 'Daily operating queue',
    },
    {
      id: 'projects',
      label: 'Projects',
      route: '/work/projects',
      icon: 'FolderKanban',
      group: 'Work',
      sourceName: 'ENT_Projects',
      description: 'Project-level planning and delivery',
    },
    {
      id: 'time-entries',
      label: 'Tracked Time',
      route: '/work/time-entries',
      icon: 'Clock',
      group: 'Work',
      sourceName: 'Time Entries',
      description: 'Review and correct tracked time',
    },
    {
      id: 'invoices',
      label: 'Invoices',
      route: '/billing/invoices',
      icon: 'Receipt',
      group: 'Billing',
      moduleId: 'oneup.invoices',
      sourceName: 'BILL_Invoices',
      description: 'Invoice status and billing review',
    },
    {
      id: 'subscriptions',
      label: 'Subscriptions',
      route: '/billing/subscriptions',
      icon: 'Repeat',
      group: 'Billing',
      moduleId: 'oneup.subscriptions',
      sourceName: 'BILL_Subscriptions',
      description: 'Recurring client billing',
    },
    {
      id: 'payments',
      label: 'Payments',
      route: '/billing/payments',
      icon: 'CreditCard',
      group: 'Billing',
      moduleId: 'oneup.payments',
      sourceName: 'BILL_Payments',
      description: 'Payment tracking and reconciliation',
    },
    {
      id: 'agents',
      label: 'Agents',
      route: '/ai-systems/agents',
      icon: 'Bot',
      group: 'AI Systems',
      moduleId: 'oneup.agents',
      sourceName: 'Ai_Agents',
      description: 'AI agent configuration and status',
    },
    {
      id: 'calls',
      label: 'Calls',
      route: '/ai-systems/calls',
      icon: 'PhoneCall',
      group: 'AI Systems',
      moduleId: 'oneup.calls',
      sourceName: 'Ai_Calls',
      description: 'AI call history and outcomes',
    },
    {
      id: 'prompts',
      label: 'Prompts',
      route: '/ai-systems/prompts',
      icon: 'MessageSquareText',
      group: 'AI Systems',
      moduleId: 'oneup.prompts',
      sourceName: 'Ai_Prompts',
      description: 'Prompt assets and versions',
    },
    {
      id: 'quick-notes',
      label: 'Communications',
      route: '/knowledge/quick-notes',
      icon: 'MessageSquareText',
      group: 'Knowledge',
      sourceName: 'Quick Notes / Gmail / Activity Logs',
      description: 'Client messages and communication timeline',
    },
    {
      id: 'md-vault',
      label: '.MD Vault',
      route: '/knowledge/md-vault',
      icon: 'BookOpenCheck',
      group: 'Knowledge',
      sourceName: 'Supabase · vault_documents',
      description: 'Markdown SOPs, documentation, prompt templates, and policy docs',
    },
    {
      id: 'whiteboard',
      label: 'Whiteboard',
      route: '/whiteboard',
      icon: 'LayoutDashboard',
      group: 'Knowledge',
      moduleId: 'oneup.whiteboard',
      description: 'AI-editable admin dashboard sandbox',
    },
    {
      id: 'settings',
      label: 'Settings',
      route: '/settings',
      icon: 'Settings',
      group: 'Settings',
      moduleId: 'oneup.settings',
      description: 'Shortcuts and preferences',
    },
    {
      id: 'alice-framework',
      label: 'Alice Framework',
      route: '/settings/alice',
      icon: 'Sparkles',
      group: 'Settings',
      moduleId: 'oneup.settings',
      description: 'Alice tools, connections, prompts, models',
    },
  ],
  modules: [
    tasksModule,
    leadsModule,
    clientsModule,
    websitesModule,
    proposalsModule,
    usersModule,
    formsModule,
    activityModule,
    copilotModule,
    whiteboardModule,
    settingsModule,
    invoicesModule,
    subscriptionsModule,
    paymentsModule,
    agentsModule,
    callsModule,
    promptsModule,
  ],
  connectors: [
    createConnectorConfig({
      id: 'oneup-airtable',
      type: 'airtable',
      env: {
        apiKey: 'AIRTABLE_API_KEY',
        defaultBaseId: 'AIRTABLE_BASE_ID',
      },
    }),
    createConnectorConfig({
      id: 'oneup-openai-logs',
      type: 'supabase',
      env: {
        url: 'SUPABASE_URL',
        serviceRoleKey: 'SUPABASE_SERVICE_ROLE_KEY',
      },
      description: 'Current AI log and Copilot cost tracking database.',
    }),
    createConnectorConfig({
      id: 'oneup-make-orchestrator',
      type: 'custom',
      env: {
        webhookUrl: 'MAKE_ORCHESTRATOR_WEBHOOK_URL',
        apiKey: 'X_MAKE_APIKEY',
      },
    }),
  ],
  tables: [
    createTableDefinition({
      id: 'oneup.tasks',
      label: 'Tasks',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'ENT_Projects',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'name', label: 'Task Name', type: 'string', sourceField: 'Task Name', required: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status' },
        { id: 'client', label: 'Client', type: 'relation', sourceField: 'OrganizationLink' },
        { id: 'targetDate', label: 'Target Date', type: 'date', sourceField: 'Target Date' },
      ],
    }),
    createTableDefinition({
      id: 'oneup.leads',
      label: 'Leads',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'Leads',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'projectName', label: 'Project Name', type: 'string', sourceField: 'Project Name', required: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status' },
        { id: 'businessName', label: 'Business Name', type: 'string', sourceField: 'Business Name' },
        { id: 'email', label: 'Email Address', type: 'string', sourceField: 'Email Address' },
        { id: 'lastModifiedAt', label: 'Last Modified', type: 'date', sourceField: 'Last Modified' },
      ],
    }),
    createTableDefinition({
      id: 'oneup.invoices',
      label: 'Invoices',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'BILL_Invoices',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'invoiceId', label: 'Invoice ID', type: 'string', sourceField: 'Invoice ID', readonly: true },
        { id: 'company', label: 'Company', type: 'string', sourceField: 'Company Name (from Clients)', readonly: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Invoice Status', readonly: true },
        { id: 'amountDue', label: 'Amount Due', type: 'currency', sourceField: 'Amount Due (raw)', readonly: true },
        { id: 'amountPaid', label: 'Amount Paid', type: 'currency', sourceField: 'Amount Paid (raw)', readonly: true },
        { id: 'createdDate', label: 'Created Date', type: 'date', sourceField: 'Created Date', readonly: true },
        { id: 'paidDate', label: 'Paid Date', type: 'date', sourceField: 'Paid Date', readonly: true },
        { id: 'stripeUrl', label: 'Stripe Hosted URL', type: 'url', sourceField: 'Stripe Hosted URL', readonly: true },
      ],
    }),
    createTableDefinition({
      id: 'oneup.subscriptions',
      label: 'Subscriptions',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'BILL_Subscriptions',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'subscriptionId', label: 'Subscription ID', type: 'string', sourceField: 'Subscription ID', readonly: true },
        { id: 'client', label: 'Client', type: 'relation', sourceField: 'Client', readonly: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Subscription Status', readonly: true },
        { id: 'billingCategory', label: 'Billing Category', type: 'string', sourceField: 'Billing Category', readonly: true },
        { id: 'price', label: 'Price', type: 'currency', sourceField: 'Price', readonly: true },
        { id: 'discountedPrice', label: 'Discounted Price', type: 'currency', sourceField: 'price with discount', readonly: true },
        { id: 'interval', label: 'Interval', type: 'string', sourceField: 'Interval', readonly: true },
        { id: 'nextInvoiceDate', label: 'Next Invoice Date', type: 'date', sourceField: 'Next Invoice Date', readonly: true },
        { id: 'currentPeriodEnd', label: 'Current Period End', type: 'string', sourceField: 'Current Period End Friendly', readonly: true },
      ],
    }),
    createTableDefinition({
      id: 'oneup.payments',
      label: 'Payments',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'BILL_Payments',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'paymentId', label: 'Payment ID', type: 'string', sourceField: 'Payment ID', readonly: true },
        { id: 'client', label: 'Client', type: 'relation', sourceField: 'Client', readonly: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status', readonly: true },
        { id: 'direction', label: 'Direction', type: 'select', sourceField: 'Direction', readonly: true },
        { id: 'platform', label: 'Platform', type: 'string', sourceField: 'Payment Platform', readonly: true },
        { id: 'method', label: 'Payment Method', type: 'string', sourceField: 'Payment Method', readonly: true },
        { id: 'grossAmount', label: 'Gross Amount', type: 'currency', sourceField: 'Gross Amount', readonly: true },
        { id: 'feeAmount', label: 'Fee Amount', type: 'currency', sourceField: 'Fee Amount', readonly: true },
        { id: 'netAmount', label: 'Net Amount', type: 'currency', sourceField: 'Net Amount', readonly: true },
        { id: 'currency', label: 'Currency', type: 'string', sourceField: 'Currency', readonly: true },
        { id: 'effectiveDate', label: 'Effective Date', type: 'date', sourceField: 'Effective Date', readonly: true },
      ],
    }),
    createTableDefinition({
      id: 'oneup.agents',
      label: 'Agents',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'Ai_Agents',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'agentName', label: 'Agent Name', type: 'string', sourceField: 'Agent Name', readonly: true },
        { id: 'profileStatus', label: 'Profile Status', type: 'select', sourceField: 'Agent Profile Status', readonly: true },
        { id: 'agentType', label: 'Agent Type', type: 'string', sourceField: 'Agent Type', readonly: true },
        { id: 'company', label: 'Company', type: 'string', sourceField: 'Company Name (from Assigned Client)', readonly: true },
        { id: 'visibility', label: 'Visibility', type: 'select', sourceField: 'Visibility', readonly: true },
        { id: 'connectionStatus', label: 'Connection Status', type: 'select', sourceField: 'Connection Status', readonly: true },
        { id: 'voiceType', label: 'Voice Type', type: 'string', sourceField: 'Voice Type', readonly: true },
        { id: 'vapiAgentId', label: 'Vapi Agent ID', type: 'string', sourceField: 'vapi_agent_id', readonly: true },
        { id: 'lastModified', label: 'Last Modified', type: 'datetime', sourceField: 'Last Modified', readonly: true },
      ],
    }),
    createTableDefinition({
      id: 'oneup.calls',
      label: 'Calls',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'Ai_Calls',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'callId', label: 'Call ID', type: 'string', sourceField: 'call_id', readonly: true },
        { id: 'caller', label: 'Caller', type: 'string', sourceField: 'caller_full_name', readonly: true },
        { id: 'business', label: 'Business', type: 'string', sourceField: 'business_name', readonly: true },
        { id: 'phone', label: 'Phone', type: 'string', sourceField: 'phone_number', readonly: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status', readonly: true },
        { id: 'type', label: 'Type Of Call', type: 'string', sourceField: 'Type Of Call', readonly: true },
        { id: 'startedAt', label: 'Started At', type: 'datetime', sourceField: 'started_at', readonly: true },
        { id: 'duration', label: 'Duration Minutes', type: 'number', sourceField: 'duration_minutes', readonly: true },
        { id: 'cost', label: 'Total Cost', type: 'currency', sourceField: 'total_cost', readonly: true },
        { id: 'endedReason', label: 'Ended Reason', type: 'string', sourceField: 'ended_reason', readonly: true },
        { id: 'agent', label: 'Agent', type: 'relation', sourceField: 'Vapi Agent_1st', readonly: true },
        { id: 'recording', label: 'Recording URL', type: 'url', sourceField: 'call_recording_url', readonly: true },
      ],
    }),
    createTableDefinition({
      id: 'oneup.prompts',
      label: 'Prompts',
      source: 'airtable',
      connectorId: 'oneup-airtable',
      tableName: 'Ai_Prompts',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'promptName', label: 'Prompt Template Name + Version', type: 'string', sourceField: 'Prompt Template Name + Version', readonly: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status', readonly: true },
        { id: 'purpose', label: 'Purpose', type: 'markdown', sourceField: 'Purpose of Prompt', readonly: true },
        { id: 'version', label: 'Version Number', type: 'number', sourceField: 'Version Number', readonly: true },
        { id: 'promptType', label: 'Prompt Type', type: 'string', sourceField: 'Prompt Type', readonly: true },
        { id: 'buildType', label: 'Prompt Build Type', type: 'string', sourceField: 'Prompt Build Type', readonly: true },
        { id: 'agents', label: 'Agents', type: 'relation', sourceField: 'Ai_Agents', readonly: true },
      ],
    }),
  ],
  featureFlags: {
    cloudflareAccessAuth: true,
    localModulesOnly: true,
    remoteModules: false,
  },
};

export default createPortalApp(config);
